import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  XMarkIcon,
  TagIcon,
  DocumentTextIcon,
  CheckIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

const ImageEditor = ({
  image,
  isOpen,
  onClose,
  onSave,
  className = ''
}) => {
  const [formData, setFormData] = useState({
    description: '',
    tags: [],
    newTag: ''
  });
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  // Modal açıkken scroll kilitle
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Modal açıldığında form verilerini yükle
  useEffect(() => {
    if (isOpen && image) {
      setFormData({
        description: image.description || '',
        tags: image.tags || [],
        newTag: ''
      });
      setErrors({});
    }
  }, [isOpen, image]);

  // Form değişikliklerini handle et
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Hata varsa temizle
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  // Etiket ekleme
  const addTag = () => {
    const tag = formData.newTag.trim();
    if (!tag) return;

    if (formData.tags.includes(tag)) {
      setErrors(prev => ({
        ...prev,
        newTag: 'Bu etiket zaten ekli'
      }));
      return;
    }

    if (formData.tags.length >= 10) {
      setErrors(prev => ({
        ...prev,
        newTag: 'Maksimum 10 etiket ekleyebilirsiniz'
      }));
      return;
    }

    setFormData(prev => ({
      ...prev,
      tags: [...prev.tags, tag],
      newTag: ''
    }));

    setErrors(prev => ({
      ...prev,
      newTag: null
    }));
  };

  // Etiket silme
  const removeTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  // Enter tuşu ile etiket ekleme
  const handleTagKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addTag();
    }
  };

  // Form validasyonu
  const validateForm = () => {
    const newErrors = {};

    if (formData.description.length > 500) {
      newErrors.description = 'Açıklama maksimum 500 karakter olabilir';
    }

    if (formData.tags.some(tag => tag.length > 20)) {
      newErrors.tags = 'Etiketler maksimum 20 karakter olabilir';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Kaydetme işlemi
  const handleSave = async () => {
    if (!validateForm()) return;

    setIsSaving(true);

    try {
      const updatedImage = {
        ...image,
        description: formData.description,
        tags: formData.tags
      };

      await onSave(updatedImage);
      onClose();
    } catch (error) {
      // Silently handle error
      setErrors(prev => ({
        ...prev,
        general: 'Görsel güncellenirken bir hata oluştu'
      }));
    } finally {
      setIsSaving(false);
    }
  };

  // Modal kapatma
  const handleClose = () => {
    if (isSaving) return;
    onClose();
  };

  if (!isOpen || !image) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000] p-4 outline-none focus:outline-none overflow-y-auto">
      <div className={`bg-white dark:bg-gray-800 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col shadow-2xl transform transition-all animate-in fade-in zoom-in duration-300 ${className}`}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <DocumentTextIcon className="w-6 h-6 text-gray-500" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Görsel Düzenle
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {image.name}
              </p>
            </div>
          </div>

          <button
            onClick={handleClose}
            disabled={isSaving}
            className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 disabled:opacity-50"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Görsel Önizleme */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Görsel Önizleme
              </h3>

              <div className="bg-gray-100 dark:bg-gray-700 rounded-lg overflow-hidden">
                <img
                  src={image.preview || image.thumbnail_url}
                  alt={image.name}
                  className="w-full h-64 object-cover"
                />
              </div>

              {/* Görsel Bilgileri */}
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500 dark:text-gray-400">Dosya Adı:</span>
                  <span className="text-gray-900 dark:text-white font-medium">{image.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500 dark:text-gray-400">Boyut:</span>
                  <span className="text-gray-900 dark:text-white">
                    {image.width && image.height ? `${image.width}x${image.height}` : 'Bilinmiyor'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500 dark:text-gray-400">Dosya Boyutu:</span>
                  <span className="text-gray-900 dark:text-white">
                    {image.size ? `${Math.round(image.size / 1024)} KB` : 'Bilinmiyor'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500 dark:text-gray-400">Yükleme Tarihi:</span>
                  <span className="text-gray-900 dark:text-white">
                    {image.uploadedAt ? new Date(image.uploadedAt).toLocaleDateString('tr-TR') : 'Bilinmiyor'}
                  </span>
                </div>
              </div>
            </div>

            {/* Düzenleme Formu */}
            <div className="space-y-6">
              {/* Açıklama */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Açıklama
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  rows={4}
                  placeholder="Görsel hakkında açıklama yazın..."
                  maxLength={500}
                />
                <div className="flex justify-between items-center mt-1">
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {formData.description.length}/500 karakter
                  </div>
                  {errors.description && (
                    <div className="text-xs text-red-500 flex items-center">
                      <ExclamationTriangleIcon className="w-3 h-3 mr-1" />
                      {errors.description}
                    </div>
                  )}
                </div>
              </div>

              {/* Etiketler */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Etiketler
                </label>

                {/* Mevcut Etiketler */}
                {formData.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {formData.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200"
                      >
                        <TagIcon className="w-3 h-3 mr-1" />
                        {tag}
                        <button
                          onClick={() => removeTag(tag)}
                          className="ml-2 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-200"
                        >
                          <XMarkIcon className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                {/* Yeni Etiket Ekleme */}
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={formData.newTag}
                    onChange={(e) => handleInputChange('newTag', e.target.value)}
                    onKeyPress={handleTagKeyPress}
                    className="flex-1 border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="Yeni etiket ekle..."
                    maxLength={20}
                  />
                  <button
                    onClick={addTag}
                    disabled={!formData.newTag.trim() || formData.tags.length >= 10}
                    className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <CheckIcon className="w-4 h-4" />
                  </button>
                </div>

                <div className="flex justify-between items-center mt-1">
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {formData.tags.length}/10 etiket
                  </div>
                  {(errors.tags || errors.newTag) && (
                    <div className="text-xs text-red-500 flex items-center">
                      <ExclamationTriangleIcon className="w-3 h-3 mr-1" />
                      {errors.tags || errors.newTag}
                    </div>
                  )}
                </div>
              </div>

              {/* Genel Hata */}
              {errors.general && (
                <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <div className="flex items-center">
                    <ExclamationTriangleIcon className="w-5 h-5 text-red-500 mr-2" />
                    <p className="text-sm text-red-700 dark:text-red-400">{errors.general}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700">
          <button
            onClick={handleClose}
            disabled={isSaving}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-md hover:bg-gray-50 dark:hover:bg-gray-500 disabled:opacity-50"
          >
            İptal
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            {isSaving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Kaydediliyor...</span>
              </>
            ) : (
              <>
                <CheckIcon className="w-4 h-4" />
                <span>Kaydet</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ImageEditor;
